import { useAuditLogRealtime, useRealtimeSubscription } from '@/hooks/useRealtime';
import { formatDistanceToNow } from 'date-fns';
import { useState, useCallback } from 'react';
import type { AuditLog } from '@/types/admin';

interface ActivityItem {
  id: string;
  type: 'audit' | 'user' | 'business' | 'flag';
  title: string;
  description: string;
  timestamp: string;
  icon: 'user' | 'business' | 'flag' | 'settings' | 'payment';
}

export default function RealtimeActivityFeed() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLive, setIsLive] = useState(true);

  // Subscribe to audit logs
  useAuditLogRealtime(
    useCallback((log: unknown) => {
      if (!isLive) return;
      const auditLog = log as AuditLog;
      const activity: ActivityItem = {
        id: auditLog.id,
        type: 'audit',
        title: formatAction(auditLog.action),
        description: `Admin action on ${auditLog.entity_type}`,
        timestamp: auditLog.created_at,
        icon: getIconForAction(auditLog.action),
      };
      setActivities(prev => [activity, ...prev].slice(0, 20));
    }, [isLive])
  );

  // Subscribe to new users
  useRealtimeSubscription<{ user_id: string; full_name?: string; email?: string; created_at: string }>(
    { table: 'user_profiles', event: 'INSERT' },
    useCallback((payload) => {
      if (!isLive) return;
      const user = payload.new;
      const newActivity: ActivityItem = {
        id: `user-${user.user_id}`,
        type: 'user',
        title: 'New User Registered',
        description: user.full_name || user.email || 'New user',
        timestamp: user.created_at,
        icon: 'user',
      };
      setActivities(prev => [newActivity, ...prev].slice(0, 20));
    }, [isLive])
  );

  // Subscribe to new businesses
  useRealtimeSubscription<{ business_id: string; business_name?: string; created_at: string }>(
    { table: 'business_profiles', event: 'INSERT' },
    useCallback((payload) => {
      if (!isLive) return;
      const biz = payload.new;
      const newActivity: ActivityItem = {
        id: `biz-${biz.business_id}`,
        type: 'business',
        title: 'New Business Registered',
        description: biz.business_name || 'New business',
        timestamp: biz.created_at,
        icon: 'business',
      };
      setActivities(prev => [newActivity, ...prev].slice(0, 20));
    }, [isLive])
  );

  // Subscribe to conversation flags
  useRealtimeSubscription<{ id: string; flag_type: string; created_at: string }>(
    { table: 'conversation_flags', event: 'INSERT' },
    useCallback((payload) => {
      if (!isLive) return;
      const flag = payload.new;
      const newActivity: ActivityItem = {
        id: `conv-flag-${flag.id}`,
        type: 'flag',
        title: 'New Conversation Flag',
        description: `ToS violation reported: ${flag.flag_type}`,
        timestamp: flag.created_at,
        icon: 'flag',
      };
      setActivities(prev => [newActivity, ...prev].slice(0, 20));
    }, [isLive])
  );

  // Subscribe to review flags
  useRealtimeSubscription<{ id: string; flag_reason: string; created_at: string }>(
    { table: 'review_flags', event: 'INSERT' },
    useCallback((payload) => {
      if (!isLive) return;
      const flag = payload.new;
      const newActivity: ActivityItem = {
        id: `review-flag-${flag.id}`,
        type: 'flag',
        title: 'New Review Flag',
        description: `Fake review reported: ${flag.flag_reason}`,
        timestamp: flag.created_at,
        icon: 'flag',
      };
      setActivities(prev => [newActivity, ...prev].slice(0, 20));
    }, [isLive])
  );

  const getIcon = (icon: string) => {
    switch (icon) {
      case 'user':
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        );
      case 'business':
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        );
      case 'flag':
        return (
          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
            </svg>
          </div>
        );
      case 'payment':
        return (
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Live Activity</h3>
          {isLive && (
            <span className="flex items-center gap-1.5 text-xs text-green-600">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          )}
        </div>
        <button
          onClick={() => setIsLive(!isLive)}
          className={`text-xs font-medium px-2 py-1 rounded ${
            isLive
              ? 'text-red-600 hover:bg-red-50'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {isLive ? 'Pause' : 'Resume'}
        </button>
      </div>

      {/* Activity List */}
      <div className="max-h-80 overflow-y-auto">
        {activities.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-500">Waiting for activity...</p>
            <p className="text-xs text-gray-400 mt-1">New events will appear here in real-time</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {activities.map((activity) => (
              <li
                key={activity.id}
                className="px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex gap-3">
                  {getIcon(activity.icon)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.title}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Helper functions
function formatAction(action: string): string {
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function getIconForAction(action: string): 'user' | 'business' | 'flag' | 'settings' | 'payment' {
  if (action.includes('user')) return 'user';
  if (action.includes('business')) return 'business';
  if (action.includes('flag') || action.includes('violation')) return 'flag';
  if (action.includes('payment') || action.includes('refund')) return 'payment';
  return 'settings';
}
